import i18n from '@/utils/i18n';
import React, { useEffect } from 'react';
import { AdyenCheckout, Dropin } from '@adyen/adyen-web/auto';
import { AdyenDropinWebProps } from './adyen-dropin-web-types';
import { StyleSheet, View } from 'react-native';
import { useUniqueErrorToast } from '@/hooks/use-unique-toasts';
import type { CoreConfiguration } from '@adyen/adyen-web';

const ADYEN_DROPIN_BASE_HEIGHT = 528;

const ADYEN_ENVIRONMENTS: NonNullable<CoreConfiguration['environment']>[] = [
  'live',
  'live-apse',
  'live-au',
  'live-in',
  'live-nea',
  'live-us',
  'test',
];

const toAdyenEnvironment = (
  environment: string,
): NonNullable<CoreConfiguration['environment']> => {
  const supportedEnvironment = ADYEN_ENVIRONMENTS.find(
    (supported) => supported === environment.toLowerCase(),
  );

  if (!supportedEnvironment) {
    throw new Error(`Unsupported Adyen environment: ${environment}`);
  }

  return supportedEnvironment;
};

export const AdyenDropinWeb: React.FC<AdyenDropinWebProps> = ({
  config,
  onChange,
  onSubmit,
  requireSavedPaymentMethod,
  webDropinRef,
}) => {
  const showErrorToast = useUniqueErrorToast();

  useEffect(() => {
    const { clientKey, countryCode, environment, paymentMethodsJson } = config;

    try {
      const paymentMethodsResponse = JSON.parse(paymentMethodsJson);
      const adyenEnvironment = toAdyenEnvironment(environment);

      const initAdyenCheckout = async () => {
        const checkout = await AdyenCheckout({
          clientKey,
          countryCode,
          environment: adyenEnvironment,
          locale: i18n.locale,
          onChange: ({ data: { paymentMethod, ...data }, ...rest }) =>
            onChange({
              ...rest,
              data: {
                ...data,
                paymentMethod: {
                  ...paymentMethod,
                  encryptedCardNumber: paymentMethod.encryptedCardNumber,
                  holderName: paymentMethod.holderName,
                  type: paymentMethod.type,
                },
              },
            }),
          onSubmit: ({ data: { paymentMethod, ...data }, isValid }) => {
            // Reset status to ready so the form isn't blocked if there was
            // a previous error when attempting to save the payment method
            webDropinRef.current?.setStatus('ready');
            onSubmit({
              data: {
                ...data,
                paymentMethod: {
                  ...paymentMethod,
                  encryptedCardNumber: paymentMethod.encryptedCardNumber,
                  type: paymentMethod.type,
                },
              },
              isValid,
            });
          },
          paymentMethodsResponse,
          showPayButton: false,
        });

        webDropinRef.current = new Dropin(checkout, {
          disableFinalAnimation: true,
          openFirstPaymentMethod: false,
          paymentMethodsConfiguration: {
            ach: {
              name: i18n.t('payment_method.ach.name'),
            },
            card: {
              billingAddressRequired: true,
              // This flag displays a checkbox for whether the user wants to save their payment method
              // If we don't allow unsaved payment methods, don't show the checkbox
              enableStoreDetails: !requireSavedPaymentMethod,
              hasHolderName: true,
              holderNameRequired: true,
              name: i18n.t('payment_method.card.name'),
            },
          },
          showStoredPaymentMethods: false,
        });

        webDropinRef.current.mount('#dropin-container');
      };

      initAdyenCheckout();
    } catch (e) {
      showErrorToast();
    }
  }, [
    config,
    onChange,
    onSubmit,
    requireSavedPaymentMethod,
    showErrorToast,
    webDropinRef,
  ]);

  return (
    <View style={styles.base} testID="adyen-dropin-web">
      <div id="dropin-container" />
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    minHeight: ADYEN_DROPIN_BASE_HEIGHT,
    // Allows Dropin dropdowns to display over continue button
    zIndex: 1,
  },
});
